import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { DesktopMenu } from './DesktopMenu';
import { MobileMenu } from './MobileMenu';

const primaryNav = [
  { name: 'Home', path: '/' },
  { name: 'The AI Whisperer', path: '/ai-whisperer' },
  { name: 'HCA', path: '/hca' },
  { name: 'Services', path: '/schedule' },
  { name: 'Speaking', path: '/speaking' },
  { name: 'Books', path: '/books' },
];

const secondaryNav = [
  { name: 'About', path: '/about' },
  { name: 'Career', path: '/career' },
  { name: 'Chef BB', path: '/chefbb' },
  { name: 'Ventures', path: '/ventures' },
  { name: 'Insights', path: '/insights' },
];

const isActive = (pathname, itemPath) => {
  if (itemPath === '/') return pathname === '/';
  return pathname.startsWith(itemPath);
};

const isSecondaryActive = (pathname) =>
  secondaryNav.some((item) => isActive(pathname, item.path));

export const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const moreRef = useRef(null);

  const handleScroll = useCallback(() => {
    setScrolled(window.scrollY > 50);
  }, [setScrolled]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    setIsOpen(false);
    setMoreOpen(false);
  }, [location, setIsOpen, setMoreOpen]);

  const handleClickOutside = useCallback((e) => {
    if (moreRef.current && !moreRef.current.contains(e.target)) setMoreOpen(false);
  }, [moreRef, setMoreOpen]);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [handleClickOutside]);

  const secondaryIsActive = isSecondaryActive(location.pathname);

  return (
    <>
      <motion.header
        className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
          scrolled ? 'glass-effect border-b border-border/30' : 'bg-transparent'
        }`}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <nav className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <img src="/assets/rdbrunet_logo.png" alt="R. Dale Brunet Logo" className="w-8 h-8 rounded-full object-cover" />
            <span className="text-xl font-bold text-white tracking-tight">R. DALE BRUNET</span>
          </Link>

          <DesktopMenu
            primaryNav={primaryNav}
            secondaryNav={secondaryNav}
            location={location}
            moreOpen={moreOpen}
            setMoreOpen={setMoreOpen}
            moreRef={moreRef}
            secondaryIsActive={secondaryIsActive}
          />

          <button
            className="lg:hidden p-2 text-white"
            onClick={() => setIsOpen(!isOpen)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </nav>
      </motion.header>

      <MobileMenu
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        primaryNav={primaryNav}
        secondaryNav={secondaryNav}
        location={location}
      />
    </>
  );
};
